import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import Logo from '../components/Logo';
import GenerateFormControl from '../components/GenerateFormControl';
import ListingInfo from '../components/ListingInfo';
import ListingTeam from '../components/ListingTeam';
import ListingEmails from '../components/ListingEmails';
import ListingChecklist from '../components/ListingChecklist';
import EmailAttachments from '../components/EmailAttachments';
import ComplianceIssueBar from '../components/ComplianceIssueBar';
import useViewerSubscription from '../hooks/useViewerSubscription';
import './ContractDetail.css';
import { getContractApiUrl } from '../utils/apiUrls';
import { getListingsApiUrl } from '../utils/apiUrls';
import { portalLoginAuthorizationParams } from '../utils/authSession';
import ChatBox from '../components/ChatBox';
import DealReplaceConfirmModal from '../components/DealReplaceConfirmModal';

const LISTINGS_URL = getListingsApiUrl();


const CONTRACT_URL = getContractApiUrl();

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const { canResolveCompliance } = useViewerSubscription();

  const [listing, setListing] = useState(null);
  const [complianceIssues, setComplianceIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sectionStates, setSectionStates] = useState({
    documents: false,
    brokerageChecklist: false,
    team: false,
    emails: false,
    listingInfo: false,
  });

  const toggleSection = (key) =>
    setSectionStates(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (isAuthenticated && id) fetchListing();
  }, [isAuthenticated, id]);

  const fetchListing = async () => {
    try {
      setLoading(true);
      setError(null);
      const [listingRes, complianceRes] = await Promise.all([
        axios.get(`${LISTINGS_URL}/${id}`, { params: { user_email: user?.email } }),
        axios.get(`${CONTRACT_URL}/compliance-issues`, {
          params: { listing_id: id, limit: 200, user_email: user?.email }
        }).catch(() => ({ data: { compliance_issues: [] } })),
      ]);
      setListing(listingRes.data);
      setComplianceIssues(
        (complianceRes.data.compliance_issues || []).filter(i => i.status === 'open')
      );
    } catch (err) {
      if (err.response?.status === 404) setError('Listing not found.');
      else setError('Failed to load listing data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const dealReplaceModal = (
    <DealReplaceConfirmModal
      search={location.search}
      address={listing?.street_address}
    />
  );

  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-content">
          <h1>Access Denied</h1>
          <p>Please log in to view listing details.</p>
          <button
            className="login-button"
            onClick={() => loginWithRedirect({
              appState: { returnTo: `${location.pathname}${location.search}` },
              authorizationParams: portalLoginAuthorizationParams(),
            })}
          >
            Go to Login
          </button>
        </div>
        {dealReplaceModal}
      </div>
    );
  }

  if (loading) {
    return (
      <>
        <LoadingSpinner />
        {dealReplaceModal}
      </>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={fetchListing} className="retry-button">Retry</button>
            <button onClick={() => navigate('/')} className="back-button">← Back</button>
          </div>
        </div>
        {dealReplaceModal}
      </div>
    );
  }

  const formatDealPrice = (value) => {
    if (value == null || value === '') return '';
    const amount = Number(String(value).replace(/[$,]/g, ''));
    if (!Number.isFinite(amount)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const sellerDisplay = (listing?.parties || [])
    .filter(p => {
      const r = (p.role || '').toLowerCase();
      return (r === 'seller' || r === 'co_seller' || r === 'co-seller') && !r.includes('agent');
    })
    .map(p => p.name).filter(Boolean).join(' & ') || '—';
  const listingPrice = formatDealPrice(listing?.listing_price);

  return (
    <div className="contract-detail-container">
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <Logo size={72} variant="arrow" />
            <div className="header-title-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h1 className="property-address-title">{listing?.street_address}</h1>
                <span className="detail-type-badge detail-type-badge--listing">Listing Agreement</span>
              </div>
              <div className="contract-parties-subtitle">
                {listingPrice ? `${sellerDisplay} listing for ${listingPrice}` : sellerDisplay}
              </div>
            </div>
          </div>
          <div className="header-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <GenerateFormControl
              parentType="listing"
              parentId={id}
              userEmail={user?.email}
              representedSides={listing?.represented_sides}
            />
            <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <ChatBox
          key={id ? `listing:${id}` : 'home'}
          userEmail={user?.email}
          userName={user?.name || user?.nickname}
          getAccessTokenSilently={getAccessTokenSilently}
          getIdTokenClaims={getIdTokenClaims}
          entityHint={id ? `listing:${id}` : undefined}
        />
        {listing && (
          <>
            {/* 0. Compliance Issues Alert */}
            <ComplianceIssueBar
              issues={complianceIssues}
              canResolve={canResolveCompliance}
              apiUrl={CONTRACT_URL}
              onResolved={(issue) => {
                setComplianceIssues((prev) => prev.filter((item) => item.id !== issue.id));
              }}
            />

            {/* 1. Transaction Documents */}
            <section className="attachments-section">
              <EmailAttachments
                contractId={null}
                listingId={listing.id}
                propertyAddress={null}
                canSendInkless={listing.can_inkless_send === true}
                canRecoverInkless={
                  listing.can_read_parent === true && listing.can_mutate === true
                }
                isExpanded={sectionStates.documents}
                onToggle={() => toggleSection('documents')}
              />
            </section>

            {/* 2. Brokerage Document Checklist */}
            <section>
              <ListingChecklist
                listingId={listing.id}
                syncStatus={listing.skyslope_sync_status}
                isExpanded={sectionStates.brokerageChecklist}
                onToggle={() => toggleSection('brokerageChecklist')}
              />
            </section>

            {/* 3. Key People */}
            <section className="team-section">
              <ListingTeam
                listingId={listing.id}
                userEmail={user?.email}
                isExpanded={sectionStates.team}
                onToggle={() => toggleSection('team')}
              />
            </section>

            {/* 4. Email History */}
            <section className="emails-section">
              <ListingEmails
                listingId={listing.id}
                userEmail={user?.email}
                isExpanded={sectionStates.emails}
                onToggle={() => toggleSection('emails')}
              />
            </section>

            {/* 5. Listing Details */}
            <section className="contract-info-section">
              <ListingInfo
                listing={listing}
                isExpanded={sectionStates.listingInfo}
                onToggle={() => toggleSection('listingInfo')}
              />
            </section>
          </>
        )}
      </main>
      {dealReplaceModal}
    </div>
  );
};

export default ListingDetail;
